import { oc } from "@orpc/contract";
import { z } from "zod";
import {
  AP_EXCEPTION_OWNER_TYPES,
  ApExceptionRuleSchema,
  ApExceptionRulesSchema,
} from "@erp/shared-types";

/**
 * AP Exception Rule Sets
 *
 * A rule set is "vocabulary + ownership": it points at a Core Enum (the
 * exception types an LLM may raise for an invoice) and carries an ordered
 * list of rules mapping an exception type to the owner who must resolve it.
 * The `match_invoice_line_items` workflow node binds one rule set by id.
 * Gated on `APExceptionRuleSet` (read to list/pick, create/update/delete to
 * manage).
 */

/** Display name for a rule's owner, resolved server-side for the editor. */
export const ApExceptionRuleOwnerSummarySchema = z.object({
  type: z.enum(AP_EXCEPTION_OWNER_TYPES),
  id: z.uuid(),
  /** Null when the referenced user / group / agent / workflow no longer exists. */
  name: z.string().nullable(),
});

export const ApExceptionRuleSetSummarySchema = z.object({
  id: z.uuid(),
  name: z.string(),
  description: z.string().nullable(),
  exceptionEnumId: z.uuid(),
  exceptionEnumName: z.string().nullable(),
  ruleCount: z.int().nonnegative(),
  updatedAt: z.iso.datetime(),
});

export const ApExceptionRuleSetSchema = ApExceptionRuleSetSummarySchema.extend({
  rules: z.array(ApExceptionRuleSchema),
  /** Owner display names, keyed by `${type}:${id}`. */
  ownerSummaries: z.array(ApExceptionRuleOwnerSummarySchema),
  /** Options of the bound enum, so the editor can label rules and warn on stale ones. */
  enumOptions: z.array(
    z.object({
      id: z.uuid(),
      name: z.string(),
      description: z.string().nullable(),
      sortOrder: z.int(),
    }),
  ),
});

export const ListApExceptionRuleSetsResponseSchema = z.object({
  ruleSets: z.array(ApExceptionRuleSetSummarySchema),
});

export const GetApExceptionRuleSetInputSchema = z.object({ id: z.uuid() });

export const CreateApExceptionRuleSetInputSchema = z.object({
  name: z.string().trim().min(1).max(120),
  description: z.string().trim().max(2000).nullable().optional(),
  exceptionEnumId: z.uuid(),
  rules: ApExceptionRulesSchema.prefault([]),
});

export const UpdateApExceptionRuleSetInputSchema = z.object({
  id: z.uuid(),
  name: z.string().trim().min(1).max(120).optional(),
  description: z.string().trim().max(2000).nullable().optional(),
  exceptionEnumId: z.uuid().optional(),
  rules: ApExceptionRulesSchema.optional(),
});

export const DeleteApExceptionRuleSetInputSchema = z.object({ id: z.uuid() });

export const apExceptionRuleSetsContracts = {
  list: oc
    .input(z.object({}))
    .output(ListApExceptionRuleSetsResponseSchema)
    .route({
      method: "GET",
      summary: "List AP exception rule sets",
      description:
        "Lists the organization's AP exception rule sets with their exception-type list and rule count. Rule sets map invoice exception types to the owner who resolves them.",
    }),
  get: oc
    .input(GetApExceptionRuleSetInputSchema)
    .output(ApExceptionRuleSetSchema)
    .route({
      method: "GET",
      summary: "Get an AP exception rule set",
      description:
        "Returns one AP exception rule set with its ordered rules, owner names, and the exception types available to assign.",
    }),
  create: oc
    .input(CreateApExceptionRuleSetInputSchema)
    .output(ApExceptionRuleSetSchema)
    .route({
      method: "POST",
      summary: "Create an AP exception rule set",
      description:
        "Creates a rule set from a name, description, exception-type list, and ordered rules routing each invoice exception type to a user, group, agent, or workflow owner.",
    }),
  update: oc
    .input(UpdateApExceptionRuleSetInputSchema)
    .output(ApExceptionRuleSetSchema)
    .route({
      method: "PUT",
      summary: "Update an AP exception rule set",
      description:
        "Updates a rule set's name, description, exception-type list, or ordered owner rules. Affects routing of invoice exceptions raised by matching workflows that use it.",
    }),
  delete: oc
    .input(DeleteApExceptionRuleSetInputSchema)
    .output(z.object({ id: z.uuid() }))
    .route({
      method: "DELETE",
      summary: "Delete an AP exception rule set",
      description:
        "Deletes an AP exception rule set. Matching workflows still bound to it stop raising exceptions rather than failing.",
    }),
} as const;

export type ApExceptionRuleOwnerSummary = z.infer<
  typeof ApExceptionRuleOwnerSummarySchema
>;
export type ApExceptionRuleSetSummary = z.infer<
  typeof ApExceptionRuleSetSummarySchema
>;
export type ApExceptionRuleSet = z.infer<typeof ApExceptionRuleSetSchema>;
export type CreateApExceptionRuleSetInput = z.infer<
  typeof CreateApExceptionRuleSetInputSchema
>;
export type UpdateApExceptionRuleSetInput = z.infer<
  typeof UpdateApExceptionRuleSetInputSchema
>;
